import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import './UserPanel.css';

const UserPanel = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Şifre değiştirme
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });

  // Editörler
  const [editors, setEditors] = useState([]);
  const [showAddEditor, setShowAddEditor] = useState(false);
  const [newEditor, setNewEditor] = useState({ username: '', email: '', password: '', maxTimelines: 0, assignProject: '' });
  const [editorsLoading, setEditorsLoading] = useState(false);
  const [myProjects, setMyProjects] = useState([]);

  // Tickets
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', category: 'other', priority: 'medium', message: '' });
  const [viewingTicket, setViewingTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');

  // Bağış ayarları
  const [donationEnabled, setDonationEnabled] = useState(false);

  useEffect(() => {
    if (user?.canCreateGroup) {
      fetchEditors();
      fetchMyProjects();
    }
    fetchTickets();
    fetchDonationSettings();
  }, [user]);

  const fetchDonationSettings = async () => {
    try {
      const res = await api.get('/api/donations/settings');
      setDonationEnabled(res.data?.enabled || false);
    } catch { setDonationEnabled(false); }
  };

  const fetchMyProjects = async () => {
    try {
      const res = await api.get('/api/timelines');
      setMyProjects(Array.isArray(res.data) ? res.data : []);
    } catch { setMyProjects([]); }
  };

  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      const res = await api.get('/api/tickets');
      setTickets(res.data);
    } catch { setTickets([]); }
    setTicketsLoading(false);
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!newTicket.subject || !newTicket.message) { setError('Konu ve mesaj zorunludur.'); return; }
    try {
      await api.post('/api/tickets', newTicket);
      setSuccess('Destek talebiniz oluşturuldu.');
      setShowNewTicket(false);
      setNewTicket({ subject: '', category: 'other', priority: 'medium', message: '' });
      fetchTickets();
    } catch (err) { setError(err.response?.data?.message || 'Oluşturulamadı.'); }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const res = await api.get(`/api/tickets/${ticketId}`);
      setViewingTicket(res.data);
    } catch { setError('Ticket yüklenemedi.'); }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage) return;
    setError('');
    try {
      const res = await api.post(`/api/tickets/${viewingTicket._id}/reply`, { message: replyMessage });
      setViewingTicket(res.data);
      setReplyMessage('');
    } catch (err) { setError(err.response?.data?.message || 'Gönderilemedi.'); }
  };

  const handleCloseTicket = async () => {
    try {
      await api.put(`/api/tickets/${viewingTicket._id}/close`);
      setViewingTicket(null);
      setSuccess('Ticket kapatıldı.');
      fetchTickets();
    } catch { setError('Kapatılamadı.'); }
  };

  const fetchEditors = async () => {
    setEditorsLoading(true);
    try {
      const res = await api.get('/api/editors');
      setEditors(res.data);
    } catch { setEditors([]); }
    setEditorsLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (passwords.newPass !== passwords.confirm) { setError('Yeni şifreler eşleşmiyor.'); return; }
    if (passwords.newPass.length < 6) { setError('Yeni şifre en az 6 karakter olmalıdır.'); return; }
    try {
      await api.put('/api/users/password', { currentPassword: passwords.current, newPassword: passwords.newPass });
      setSuccess('Şifre başarıyla güncellendi.');
      setPasswords({ current: '', newPass: '', confirm: '' });
    } catch (err) { setError(err.response?.data?.message || 'Şifre güncellenemedi.'); }
  };

  const handleAddEditor = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!newEditor.email || !newEditor.password) { setError('E-posta ve şifre zorunludur.'); return; }
    try {
      const res = await api.post('/api/editors', {
        username: newEditor.username,
        email: newEditor.email,
        password: newEditor.password,
        maxTimelines: parseInt(newEditor.maxTimelines, 10) || 0,
        assignProject: newEditor.assignProject || null,
      });
      setSuccess('Editör eklendi.');
      setShowAddEditor(false);
      setNewEditor({ username: '', email: '', password: '', maxTimelines: 0, assignProject: '' });
      fetchEditors();
    } catch (err) { setError(err.response?.data?.message || 'Editör eklenemedi.'); }
  };

  const handleDeleteEditor = async (editorId, username) => {
    if (!window.confirm(`"${username}" editörünü silmek istediğinizden emin misiniz?`)) return;
    setError(''); setSuccess('');
    try {
      await api.delete(`/api/editors/${editorId}`);
      setSuccess('Editör silindi.');
      fetchEditors();
    } catch (err) { setError(err.response?.data?.message || 'Editör silinemedi.'); }
  };

  return (
    <div className="user-panel">
      <div className="user-panel-header">
        <h1>Hesabım</h1>
        <div className="user-panel-nav">
          <button className={activeTab === 'account' ? 'active' : ''} onClick={() => setActiveTab('account')}>Hesap</button>
          <button className={activeTab === 'password' ? 'active' : ''} onClick={() => setActiveTab('password')}>Şifre</button>
          {user?.canCreateGroup && (
            <button className={activeTab === 'editors' ? 'active' : ''} onClick={() => setActiveTab('editors')}>Editörlerim</button>
          )}
          <button className={activeTab === 'tickets' ? 'active' : ''} onClick={() => setActiveTab('tickets')}>Destek</button>
          {donationEnabled && (
            <button onClick={() => navigate('/bagis')} style={{ backgroundColor: '#059669', color: '#fff' }}>Bağış Yap</button>
          )}
          <button onClick={() => navigate('/gosterge-paneli')} style={{ backgroundColor: '#374151', color: '#d1d5db' }}>Çizelgelerim</button>
          <button onClick={logout} style={{ backgroundColor: '#dc2626', color: '#fff' }}>Çıkış</button>
        </div>
      </div>

      <div className="user-panel-content">
        {success && <div className="user-panel-success">{success}</div>}
        {error && <div className="user-panel-error">{error}</div>}

        {/* Hesap Bilgileri */}
        {activeTab === 'account' && (
          <div className="user-panel-card">
            <h3>Hesap Bilgileri</h3>
            <div className="user-panel-field">
              <label>Kullanıcı Adı</label>
              <p>{user?.username}</p>
            </div>
            <div className="user-panel-field">
              <label>E-posta</label>
              <p>{user?.email}</p>
            </div>
            <div className="user-panel-field">
              <label>Rol</label>
              <p>{user?.isSuperAdmin ? 'Süper Admin' : user?.isAdmin ? 'Admin' : user?.role === 'editor' ? 'Editör' : 'Kullanıcı'}</p>
            </div>
            <div className="user-panel-field">
              <label>Zaman Çizelgesi Limiti</label>
              <p>{user?.isSuperAdmin ? 'Sınırsız' : (user?.maxTimelines || 5)}</p>
            </div>
            {user?.canCreateGroup && (
              <div className="user-panel-field">
                <label>Editör Limiti</label>
                <p>{user?.maxEditors || 0}</p>
              </div>
            )}
          </div>
        )}

        {/* Şifre Değiştir */}
        {activeTab === 'password' && (
          <div className="user-panel-card">
            <h3>Şifre Değiştir</h3>
            <form onSubmit={handlePasswordChange}>
              <div className="user-panel-form-group">
                <label>Mevcut Şifre</label>
                <input type="password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} required />
              </div>
              <div className="user-panel-form-group">
                <label>Yeni Şifre</label>
                <input type="password" value={passwords.newPass} onChange={(e) => setPasswords({ ...passwords, newPass: e.target.value })} required />
              </div>
              <div className="user-panel-form-group">
                <label>Yeni Şifre (Tekrar)</label>
                <input type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} required />
              </div>
              <button type="submit" className="user-panel-btn user-panel-btn-primary">Şifreyi Güncelle</button>
            </form>
          </div>
        )}

        {/* Editörlerim */}
        {activeTab === 'editors' && user?.canCreateGroup && (
          <div className="user-panel-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #374151' }}>
              <h3 style={{ margin: 0, border: 'none', padding: 0 }}>Editörlerim ({editors.length} / {user?.maxEditors || 0})</h3>
              <button className="user-panel-btn user-panel-btn-primary" onClick={() => setShowAddEditor(!showAddEditor)}>+ Editör Ekle</button>
            </div>

            {showAddEditor && (
              <form onSubmit={handleAddEditor} style={{ padding: '1rem', backgroundColor: '#374151', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                <div className="user-panel-form-group">
                  <label>Kullanıcı Adı (opsiyonel)</label>
                  <input type="text" value={newEditor.username} onChange={(e) => setNewEditor({ ...newEditor, username: e.target.value })} placeholder="Boş bırakırsan otomatik oluşturulur" />
                </div>
                <div className="user-panel-form-group">
                  <label>E-posta</label>
                  <input type="email" value={newEditor.email} onChange={(e) => setNewEditor({ ...newEditor, email: e.target.value })} required />
                </div>
                <div className="user-panel-form-group">
                  <label>Şifre</label>
                  <input type="password" value={newEditor.password} onChange={(e) => setNewEditor({ ...newEditor, password: e.target.value })} required />
                </div>
                <div className="user-panel-form-group">
                  <label>Çizelge Limiti</label>
                  <input type="number" value={newEditor.maxTimelines || 0} onChange={(e) => setNewEditor({ ...newEditor, maxTimelines: e.target.value })} min="0" />
                </div>
                <div className="user-panel-form-group">
                  <label>Proje Atama</label>
                  <select value={newEditor.assignProject || ''} onChange={(e) => setNewEditor({ ...newEditor, assignProject: e.target.value })} style={{ width: '100%', padding: '0.5rem', backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '0.5rem', color: '#fff' }}>
                    <option value="">Proje atama (kendi projesi olsun)</option>
                    {myProjects.map(p => (
                      <option key={p._id} value={p._id}>{p.title} (ortak çalışma)</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" className="user-panel-btn user-panel-btn-primary">Ekle</button>
                  <button type="button" className="user-panel-btn user-panel-btn-secondary" onClick={() => setShowAddEditor(false)}>İptal</button>
                </div>
              </form>
            )}

            {editorsLoading ? (
              <p style={{ color: '#9ca3af' }}>Yükleniyor...</p>
            ) : editors.length > 0 ? (
              editors.map(editor => (
                <div key={editor._id} className="user-panel-editor-item">
                  <div>
                    <strong style={{ color: '#f3f4f6' }}>{editor.username}</strong>
                    <span style={{ marginLeft: '0.75rem', color: '#9ca3af', fontSize: '0.8rem' }}>{editor.email}</span>
                  </div>
                  <button className="user-panel-btn user-panel-btn-danger" onClick={() => handleDeleteEditor(editor._id, editor.username)} style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem' }}>Sil</button>
                </div>
              ))
            ) : (
              <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Henüz editör eklenmemiş.</p>
            )}
          </div>
        )}

        {/* Destek Tickets */}
        {activeTab === 'tickets' && !viewingTicket && (
          <div className="user-panel-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #374151' }}>
              <h3 style={{ margin: 0, border: 'none', padding: 0 }}>Destek Taleplerim ({tickets.length})</h3>
              <button className="user-panel-btn user-panel-btn-primary" onClick={() => setShowNewTicket(!showNewTicket)}>+ Yeni Talep</button>
            </div>

            {showNewTicket && (
              <form onSubmit={handleCreateTicket} style={{ padding: '1rem', backgroundColor: '#374151', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                <div className="user-panel-form-group">
                  <label>Konu</label>
                  <input type="text" value={newTicket.subject} onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })} required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="user-panel-form-group">
                    <label>Kategori</label>
                    <select value={newTicket.category} onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })} style={{ width: '100%', padding: '0.5rem', backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '0.5rem', color: '#fff' }}>
                      <option value="technical">Teknik</option>
                      <option value="account">Hesap</option>
                      <option value="content">İçerik</option>
                      <option value="feature">Özellik Talebi</option>
                      <option value="other">Diğer</option>
                    </select>
                  </div>
                  <div className="user-panel-form-group">
                    <label>Öncelik</label>
                    <select value={newTicket.priority} onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })} style={{ width: '100%', padding: '0.5rem', backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '0.5rem', color: '#fff' }}>
                      <option value="low">Düşük</option>
                      <option value="medium">Orta</option>
                      <option value="high">Yüksek</option>
                      <option value="urgent">Acil</option>
                    </select>
                  </div>
                </div>
                <div className="user-panel-form-group">
                  <label>Mesaj</label>
                  <textarea value={newTicket.message} onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })} rows="4" style={{ width: '100%', padding: '0.5rem', backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '0.5rem', color: '#fff', resize: 'vertical' }} required />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" className="user-panel-btn user-panel-btn-primary">Gönder</button>
                  <button type="button" className="user-panel-btn user-panel-btn-secondary" onClick={() => setShowNewTicket(false)}>İptal</button>
                </div>
              </form>
            )}

            {ticketsLoading ? <p style={{ color: '#9ca3af' }}>Yükleniyor...</p> : tickets.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {tickets.map(t => (
                  <div key={t._id} onClick={() => handleViewTicket(t._id)} style={{ padding: '0.75rem 1rem', backgroundColor: '#374151', borderRadius: '0.5rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderLeft: t.status === 'open' ? '3px solid #3b82f6' : t.status === 'closed' ? '3px solid #6b7280' : '3px solid #f59e0b' }}>
                    <div>
                      <strong style={{ color: '#f3f4f6', fontSize: '0.85rem' }}>{t.subject}</strong>
                      <p style={{ color: '#9ca3af', fontSize: '0.7rem', margin: '0.2rem 0 0 0' }}>{t.ticketNumber} • {new Date(t.updatedAt).toLocaleDateString('tr-TR')}</p>
                    </div>
                    <span style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', borderRadius: '9999px', backgroundColor: t.status === 'open' ? 'rgba(59,130,246,0.15)' : t.status === 'closed' ? 'rgba(107,114,128,0.15)' : 'rgba(245,158,11,0.15)', color: t.status === 'open' ? '#60a5fa' : t.status === 'closed' ? '#9ca3af' : '#fbbf24' }}>
                      {t.status === 'open' ? 'Açık' : t.status === 'in-progress' ? 'İşlemde' : t.status === 'waiting' ? 'Bekliyor' : t.status === 'resolved' ? 'Çözüldü' : 'Kapalı'}
                    </span>
                  </div>
                ))}
              </div>
            ) : <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Henüz destek talebiniz yok.</p>}
          </div>
        )}

        {/* Ticket Detay */}
        {activeTab === 'tickets' && viewingTicket && (
          <div className="user-panel-card">
            <button onClick={() => setViewingTicket(null)} className="user-panel-back">← Geri</button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, border: 'none', padding: 0 }}>{viewingTicket.subject}</h3>
                <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: '0.25rem 0 0 0' }}>{viewingTicket.ticketNumber} • {viewingTicket.status === 'open' ? 'Açık' : viewingTicket.status === 'closed' ? 'Kapalı' : 'İşlemde'}</p>
              </div>
              {viewingTicket.status !== 'closed' && (
                <button onClick={handleCloseTicket} className="user-panel-btn user-panel-btn-secondary" style={{ fontSize: '0.75rem' }}>Kapat</button>
              )}
            </div>

            {/* Mesajlar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem', maxHeight: '400px', overflowY: 'auto', padding: '0.5rem' }}>
              {viewingTicket.messages?.map((msg, i) => (
                <div key={i} style={{ padding: '0.75rem', borderRadius: '0.5rem', backgroundColor: msg.senderRole === 'admin' ? '#1e3a5f' : '#374151', borderLeft: msg.senderRole === 'admin' ? '3px solid #3b82f6' : '3px solid #4b5563' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: msg.senderRole === 'admin' ? '#60a5fa' : '#d1d5db' }}>{msg.sender?.username || 'Kullanıcı'} {msg.senderRole === 'admin' && '(Destek)'}</span>
                    <span style={{ fontSize: '0.65rem', color: '#6b7280' }}>{new Date(msg.createdAt).toLocaleString('tr-TR')}</span>
                  </div>
                  <p style={{ color: '#e2e8f0', fontSize: '0.85rem', margin: 0, lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                </div>
              ))}
            </div>

            {/* Yanıt */}
            {viewingTicket.status !== 'closed' && (
              <form onSubmit={handleReply} style={{ display: 'flex', gap: '0.5rem' }}>
                <textarea value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} placeholder="Yanıtınızı yazın..." rows="2" style={{ flex: 1, padding: '0.5rem', backgroundColor: '#374151', border: '1px solid #4b5563', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem', resize: 'vertical' }} required />
                <button type="submit" className="user-panel-btn user-panel-btn-primary" style={{ alignSelf: 'flex-end' }}>Gönder</button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPanel;
